import { SortAscendingIcon, SortDescendingIcon } from '@phosphor-icons/react'
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import * as RadixRadioGroup from '@radix-ui/react-radio-group'
import { SplitOutlineButton, SplitOutlineButtonSegment } from '../buttons/split-outline-button.tsx'
import { RadioItem } from '../input/radio'
import * as styles from './sort-dropdown-menu.css'

export type SortOption<T = string> = {
  value: T
  label: string
}

export type SortDropdownMenuProps<T = string> = {
  options: SortOption<T>[]
  selectedValue: T
  onSortChange: (value: T) => unknown
  isAscending: boolean
  onSortDirectionChange: (isAscending: boolean) => unknown
  className?: string
  placeholder?: string
}

export const SortDropdownMenu = <T extends string>({
  options,
  selectedValue,
  onSortChange,
  isAscending,
  onSortDirectionChange,
  className,
  placeholder = 'Sort by',
}: SortDropdownMenuProps<T>) => {
  const selectedOption = options.find(option => option.value === selectedValue)
  const displayLabel = selectedOption ? `${placeholder}: ${selectedOption.label}` : placeholder

  const handleArrowClick = () => {
    onSortDirectionChange(!isAscending)
  }

  return (
    <RadixDropdownMenu.Root>
      <SplitOutlineButton className={className} aria-label="Sort">
        <RadixDropdownMenu.Trigger asChild>
          <SplitOutlineButtonSegment position="start" aria-label="Sort by">
            {displayLabel}
          </SplitOutlineButtonSegment>
        </RadixDropdownMenu.Trigger>
        <SplitOutlineButtonSegment
          position="end"
          onClick={handleArrowClick}
          aria-label={isAscending ? 'Sort ascending' : 'Sort descending'}
        >
          {isAscending ? <SortAscendingIcon size={16} /> : <SortDescendingIcon size={16} />}
        </SplitOutlineButtonSegment>
      </SplitOutlineButton>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content className={styles.dropdownContent} sideOffset={5}>
          <RadixRadioGroup.Root
            value={selectedValue}
            onValueChange={value => onSortChange(value as T)}
            className={styles.radioGroup}
          >
            {options.map(option => (
              <RadioItem key={option.value} value={option.value} label={option.label} id={`${option.value}-radio`} />
            ))}
          </RadixRadioGroup.Root>
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  )
}
